<template>
  <div class="custom-hint hint-wrapper" ref="wrapper" @mouseenter="show" @mouseleave="hide">
    <slot name="trigger">
      <span class="hint-trigger-default">?</span>
    </slot>

    <transition name="hint-fade">
      <div
        v-if="visible && ($slots.default || text)"
        class="hint-container"
        :class="[
          `hint-${direction}`,
          `hint-color-${color}`,
          { 'hint-rtl': isRTL }
        ]"
        :style="customStyle"
        ref="hint"
        role="tooltip"
      >
        <div class="hint-arrow" :class="[`arrow-direction-${hintArrowDirection}`]"></div>
        <div class="hint-content">
          <slot v-if="$slots.default" />
          <div v-else>{{ text }}</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'customHint',
  props: {
    text: {
      type: String,
      default: ''
    },
    direction: {
      type: String,
      default: 'top',
      validator: value => ['top', 'bottom', 'left', 'right'].includes(value)
    },
    color: {
      type: String,
      default: 'dark',
      validator: value => [
        'primary', 'success', 'warning', 'danger',
        'dark', 'gray', 'info', 'light'
      ].includes(value)
    },
    customColor: {
      type: String,
      default: null
    },
    customTextColor: {
      type: String,
      default: null
    },
    offset: {
      type: Number,
      default: 6
    },
    delay: {
      type: Number,
      default: 200
    },
    trigger: {
      type: String,
      default: 'hover',
      validator: value => ['hover', 'click', 'focus', 'manual'].includes(value)
    },
    value: {
      type: Boolean,
      default: false
    },
    maxWidth: {
      type: [Number, String],
      default: 300
    },
    // اضافه شده: برای انتقال به body
    appendToBody: {
      type: Boolean,
      default: true
    },
    // اضافه شده: برای z-index سفارشی
    zIndex: {
      type: Number,
      default: 999999
    }
  },

  data() {
    return {
      visible: false,
      isRTL: false,
      timer: null,
      // نگهدارنده المان hint که به body اضافه میشه
      hintElement: null,
      hintArrowDirection: 'left',
      // نگهدارنده موقعیت برای نمایش
      hintStyle: {}
    }
  },

  computed: {
    customStyle() {
      const style = {
        zIndex: this.zIndex
      }

      if (this.customColor) {
        style.backgroundColor = this.customColor
        style.borderColor = this.customColor
      }

      if (this.customTextColor) {
        style.color = this.customTextColor
      }

      if (this.maxWidth) {
        style.maxWidth = typeof this.maxWidth === 'number'
          ? `${this.maxWidth}px`
          : this.maxWidth
      }

      return style
    },

    isManual() {
      return this.trigger === 'manual'
    },

    // کلاس‌های hint برای استفاده در المان منتقل شده
    hintClasses() {
      return [
        'hint-container',
        `hint-${this.direction}`,
        `hint-color-${this.color}`,
        { 'hint-rtl': !this.$vs.rtl }
      ]
    }
  },

  watch: {
    value(newVal) {
      if (this.isManual) {
        this.visible = newVal
        if (newVal) {
          this.$nextTick(() => {
            this.updatePosition()
          })
        }
      }
    },
    text() {
      this.visible = true
      this.$nextTick(() => {
        this.updatePosition()
      })
    },
    visible(newVal) {
      // وقتی visible تغییر کرد، المان رو مدیریت کن
      if (newVal) {
        this.mountHintToBody()
      } else {
        // با تاخیر unmount کن تا انیمیشن تمام بشه
        setTimeout(() => {
          if (!this.visible) {
            this.unmountHintFromBody()
          }
        }, 200)
      }
    }
  },

  mounted() {
    if (this.trigger === 'click') {
      this.$el.addEventListener('click', this.toggle)
    } else if (this.trigger === 'focus') {
      const triggerEl = this.$refs.wrapper.querySelector('[tabindex]') || this.$el
      triggerEl.addEventListener('focus', this.show)
      triggerEl.addEventListener('blur', this.hide)
    }

    window.addEventListener('scroll', this.updatePosition, true)
    window.addEventListener('resize', this.updatePosition)
  },

  beforeDestroy() {
    clearTimeout(this.timer)
    window.removeEventListener('scroll', this.updatePosition, true)
    window.removeEventListener('resize', this.updatePosition)

    if (this.trigger === 'click') {
      this.$el.removeEventListener('click', this.toggle)
    }

    // پاکسازی المان از body
    this.unmountHintFromBody()
  },

  methods: {
    checkRTL() {
      const dir = window.getComputedStyle(this.$el).direction
      this.isRTL = dir === 'rtl'
    },

    // متد جدید: اضافه کردن hint به body
    mountHintToBody() {
      if (!this.appendToBody) return

      this.$nextTick(() => {
        const hint = this.$refs.hint
        if (!hint) return

        this.hintElement = hint
        // انتقال به body
        document.body.appendChild(hint)

        // بعد از انتقال، موقعیت رو دوباره محاسبه کن
        this.$nextTick(() => {
          this.updatePosition()
        })
      })
    },

    // متد جدید: حذف hint از body
    unmountHintFromBody() {
      if (this.hintElement && this.hintElement.parentNode === document.body) {
        document.body.removeChild(this.hintElement)
        this.hintElement = null
      }
    },

    show() {
      if (this.isManual) return
      clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.visible = true
        this.$emit('show')
      }, this.delay)
    },

    hide() {
      if (this.isManual) return
      clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.visible = false
        this.$emit('hide')
      }, 100)
    },

    toggle() {
      if (this.isManual) return
      this.visible = !this.visible
      if (this.visible) {
        this.$emit('show')
      } else {
        this.$emit('hide')
      }
    },

    updatePosition() {
      if (!this.visible) return

      const wrapper = this.$refs.wrapper
      // وقتی به body منتقل شده، از hintElement استفاده کن
      const hint = this.hintElement || this.$refs.hint

      if (!wrapper || !hint) return

      const wrapperRect = wrapper.getBoundingClientRect()
      const hintRect = hint.getBoundingClientRect()

      let top = 0
      let left = 0

      switch (this.direction) {
        case 'top':
          top = wrapperRect.top - hintRect.height - this.offset
          left = wrapperRect.left + (wrapperRect.width - hintRect.width) / 2
          break
        case 'bottom':
          top = wrapperRect.bottom + this.offset
          left = wrapperRect.left + (wrapperRect.width - hintRect.width) / 2
          break
        case 'left':
          top = wrapperRect.top + (wrapperRect.height - hintRect.height) / 2
          left = wrapperRect.left - hintRect.width - this.offset
          break
        case 'right':
          top = wrapperRect.top + (wrapperRect.height - hintRect.height) / 2
          left = wrapperRect.right + this.offset
          break
      }

      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      // تنظیم افقی
      if (left < 10) {
        left = 10
        hint.style.setProperty('--arrow-offset', `${wrapperRect.left - 10 + wrapperRect.width / 2}px`)
      } else if (left + hintRect.width > viewportWidth - 10) {
        left = viewportWidth - hintRect.width - 10
        // hint.style.setProperty('--arrow-offset', `${wrapperRect.right - 10 + wrapperRect.width / 2}px`)
        this.hintArrowDirection = 'right'
        hint.style.setProperty('--arrow-offset', `${viewportWidth - wrapperRect.left - wrapperRect.width}px`)
      } else {
        const centerOffset = wrapperRect.left + wrapperRect.width / 2 - left
        hint.style.setProperty('--arrow-offset', `${centerOffset}px`)
      }

      // تنظیم عمودی
      if (top < 10) {
        top = 10
      } else if (top + hintRect.height > viewportHeight - 10) {
        top = viewportHeight - hintRect.height - 10
      }

      // چون به body منتقل شده، position: fixed کافیه
      hint.style.top = `${top}px`
      hint.style.left = `${left}px`
      hint.style.zIndex = this.zIndex
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../assets/scss/vuexy/variables";

/*! rtl:begin:ignore */
.custom-hint {
  width: 100%;
}

.hint-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

.hint-container {
  position: fixed;
  z-index: 999999;
  padding: 0.8rem 1.2rem;
  border-radius: 0.6rem;
  font-size: 1.4rem;
  line-height: 1.5;
  max-width: 30rem;
  word-wrap: break-word;
  box-shadow: 0 0.4rem 1.2rem rgba(0, 0, 0, 0.15);
  pointer-events: none;
  transition: opacity 0.2s ease;
}

.hint-content {
  position: relative;
  z-index: 1;
}

.hint-arrow {
  position: absolute;
  width: 0.8rem;
  height: 0.8rem;
  background: inherit;
  transform: rotate(45deg);
  z-index: 0;
}

.hint-top .hint-arrow {
  bottom: -0.4rem;
  transform: translateX(-50%) rotate(45deg);

  &.arrow-direction-left {
    left: var(--arrow-offset, 50%);
  }

  &.arrow-direction-right {
    transform: translateX(calc(var(--arrow-offset, 50%) * -1 - 50%)) rotate(45deg);
  }
}

.hint-bottom .hint-arrow {
  top: -0.4rem;
  transform: translateX(-50%) rotate(45deg);

  &.arrow-direction-left {
    left: var(--arrow-offset, 50%);
  }

  &.arrow-direction-right {
    transform: translateX(calc(var(--arrow-offset, 50%) * -1 - 50%)) rotate(45deg);
  }
}

.hint-left .hint-arrow {
  right: -0.4rem;
  top: var(--arrow-offset, 50%);
  transform: translateY(-50%) rotate(45deg);
}

.hint-right .hint-arrow {
  left: -0.4rem;
  top: var(--arrow-offset, 50%);
  transform: translateY(-50%) rotate(45deg);
}

.hint-fade-enter-active,
.hint-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.hint-fade-enter,
.hint-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

$colors: (
  primary: $primary,
  success: $success,
  warning: $warning,
  danger: $danger,
  dark: #2c3e50,
  info: #1abc9c
);

@each $name, $color in $colors {
  .hint-color-#{$name} {
    background-color: $color;
    color: #fff;
    border-color: $color;
  }
}

.hint-color-gray {
  background-color: #95a5a6;
  color: #000;
  border-color: #95a5a6;
}

.hint-color-light {
  background-color: #ecf0f1;
  color: #2c3e50;
  border-color: #ecf0f1;
}

.hint-rtl {
  direction: rtl;
  text-align: right;
}

@media (max-width: 768px) {
  .hint-container {
    max-width: 28rem !important;
    font-size: 1.3rem;
    padding: 0.6rem 1rem;
  }
}

@media (max-width: 480px) {
  .hint-container {
    max-width: 20rem !important;
    font-size: 1.2rem;
    padding: 0.5rem 0.8rem;
  }
}
/*! rtl:end:ignore */
</style>
